<template>
  <v-container class="menu-container">
  

    <div class="menu-section nav-section">
      <nav class="menu-nav">
        <router-link
          to="/admin"
          class="menu-link primary-link"
        >
          <v-icon size="20" class="mr-2">mdi-shield-account</v-icon>
          {{ t('menus.default.links.adminOverview') }}
        </router-link>
        <router-link
          to="/admin/account"
          class="menu-link"
        >
          <v-icon size="20" class="mr-2">mdi-account-cog-outline</v-icon>
          {{ t('menus.default.links.adminAccount') }}
        </router-link>
        <router-link
          to="/admin/klarvertrag"
          class="menu-link"
        >
          <v-icon size="20" class="mr-2">mdi-file-search</v-icon>
          {{ t('menus.default.links.adminKlarvertragSearch') }}
        </router-link>
      </nav>
    </div>

    <div
      v-if="!isLoadingProducts && myProducts.length > 0"
      class="menu-section my-products"
    >
      <h3 class="section-title">
        <v-icon size="18" class="mr-2" color="#EA5545">mdi-package-variant</v-icon>
        {{ t('menus.default.sections.myProducts') }}
      </h3>
      <div class="products-list">
        <router-link
          v-for="product in myProducts"
          :key="product.myp_id"
          :to="{ name: 'vertrag', params: { id: product.myp_id } }"
          class="product-item"
        >
          <v-icon size="16" color="#EA5545" class="mr-2">mdi-package</v-icon>
          <span class="product-name">{{ product.prod_name }}</span>
        </router-link>
      </div>
    </div>

    <div
      class="menu-section recent-tickets"
      v-if="!isLoadingUnread && unreadTickets.length > 0"
    >
      <h3 class="section-title">
        <v-icon size="18" class="mr-2" color="#EA5545">mdi-clock-outline</v-icon>
        {{ t('menus.default.sections.unreadTickets') }}
      </h3>
      <div class="tickets-list">
        <router-link
          v-for="ticket in unreadTickets"
          :key="ticket.sup_id"
          :to="`/support/${ticket.sup_id}`"
          :class="['ticket-item', { 'has-unread': ticket.unread_count > 0 }]"
        >
          <div class="ticket-header">
            <div class="ticket-id-container">
              <span class="ticket-id">#{{ ticket.sup_id }}</span>
              <v-badge
                v-if="ticket.unread_count > 0"
                :content="ticket.unread_count"
                color="#EA5545"
                inline
                class="unread-badge-small"
              />
            </div>
            <v-chip
              :color="getStatusColor(ticket.sup_status)"
              size="x-small"
              class="status-chip"
            >
              {{ getStatusText(ticket.sup_status) }}
            </v-chip>
          </div>
          <p class="ticket-title">{{ truncateText(ticket.sup_title, 45) }}</p>
          <div class="ticket-footer">
            <span class="ticket-date">{{ formatDate(ticket.sup_created_date) }}</span>
            <span v-if="ticket.unread_count > 0" class="new-messages-indicator">
              <v-icon size="12" color="#EA5545">mdi-circle</v-icon>
              {{ t('menus.default.states.newIndicator') }}
            </span>
          </div>
        </router-link>
      </div>
    </div>

    <div
      class="menu-section support-preview"
      v-if="!isLoadingRecent && recentTickets.length > 0"
    >
      <h3 class="section-title">
        <v-icon size="18" class="mr-2" color="#EA5545">mdi-ticket</v-icon>
        {{ t('menus.default.actions.viewSupports') }}
      </h3>
      <div class="tickets-list compact">
        <router-link
          v-for="ticket in recentTickets"
          :key="ticket.sup_id"
          :to="`/support/${ticket.sup_id}`"
          class="ticket-item"
        >
          <div class="ticket-header">
            <div class="ticket-id-container">
              <span class="ticket-id">#{{ ticket.sup_id }}</span>
            </div>
            <v-chip
              :color="getStatusColor(ticket.sup_status)"
              size="x-small"
              class="status-chip"
            >
              {{ getStatusText(ticket.sup_status) }}
            </v-chip>
          </div>
          <p class="ticket-title">{{ truncateText(ticket.sup_title, 60) }}</p>
          <div class="ticket-footer">
            <span class="ticket-date">{{ formatDate(ticket.sup_created_date) }}</span>
          </div>
        </router-link>
      </div>

      <div class="section-link-card">
        <router-link to="/support" class="section-link">
          {{ t('menus.default.actions.viewSupports') }}
        </router-link>
      </div>
    </div>
  </v-container>
</template>

<script setup>
import { ref, onMounted, inject, computed } from 'vue'
import { useLanguage } from '@/composables/useLanguage'

const baseCalls = inject('baseCalls')
const { languageStore, t } = useLanguage()

const unreadTickets = ref([])
const recentTickets = ref([])
const myProducts = ref([])
const isLoadingUnread = ref(false)
const isLoadingRecent = ref(false)
const isLoadingProducts = ref(false)

const currentLocale = computed(() => {
  const lang = languageStore?.currentLanguage?.value || 'de'
  return lang === 'de' ? 'de-DE' : 'en-US'
})

function getStatusColor(status) {
  const colors = {
    0: 'orange',
    1: 'green',
    2: 'grey',
    3: 'red'
  }
  return colors[status] || 'grey'
}

function getStatusText(status) {
  const texts = {
    0: 'supportIndex.status.open',
    1: 'supportIndex.status.resolved',
    2: 'supportIndex.status.closed',
    3: 'supportIndex.status.urgent'
  }
  const key = texts[status] || 'supportIndex.status.unknown'
  return t(key)
}

function formatDate(dateString) {
  if (!dateString || dateString === '1973-01-01 00:00:00') return t('common.labels.notAvailable')

  const date = new Date(dateString)
  const now = new Date()
  const diffInMs = now - date
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInMinutes < 1) return t('menus.default.time.justNow')
  if (diffInMinutes < 60) return t('menus.default.time.minutesAgo', { value: diffInMinutes })
  if (diffInHours < 24) return t('menus.default.time.hoursAgo', { value: diffInHours })
  if (diffInDays < 7) return t('menus.default.time.daysAgo', { value: diffInDays })

  return date.toLocaleDateString(currentLocale.value, {
    month: 'short',
    day: 'numeric'
  })
}

function truncateText(text, maxLength) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

async function loadUnreadTickets() {
  isLoadingUnread.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getunreadsupports',
      limit: 3
    })

    if (response.data.success && response.data.supports) {
      unreadTickets.value = response.data.supports || []
    } else {
      unreadTickets.value = []
    }
  } catch (err) {
    console.error('Error loading unread tickets:', err)
    unreadTickets.value = []
  } finally {
    isLoadingUnread.value = false
  }
}

async function loadRecentTickets() {
  isLoadingRecent.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getAllMySupports'
    })

    if (response.data.success && response.data.supports) {
      recentTickets.value = response.data.supports.slice(0, 3)
    } else {
      recentTickets.value = []
    }
  } catch (err) {
    console.error('Error loading recent tickets:', err)
    recentTickets.value = []
  } finally {
    isLoadingRecent.value = false
  }
}

async function loadMyProducts() {
  isLoadingProducts.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getmyproducts'
    })

    if (response.data.success && response.data.products) {
      myProducts.value = response.data.products
    }
  } catch (err) {
    console.error('Error loading products:', err)
  } finally {
    isLoadingProducts.value = false
  }
}

onMounted(() => {
  loadUnreadTickets()
  loadRecentTickets()
  loadMyProducts()
})
</script>

<style scoped src="@/assets/styles/sfc/components/sites/modules/MenueAdminMenu.css"></style>
